import { useState, useEffect } from 'react';
import { api } from './api';

// =============================================================================
//  QuickBooksSetup ("Books" tab) — Complete tier
//  -------------------------------------------------------------------------
//  Connect QuickBooks Online (per-user Intuit OAuth), then show the synced
//  Profit & Loss: income, expenses, true net profit, and an estimated-tax
//  set-aside, per period. Mirrors UpworkSetup. Connect is a full-page redirect;
//  the callback returns to /?qbo_connected=1.
// =============================================================================

const GRAIN_LABEL = { mtd: 'Month to date', qtd: 'Quarter to date', ytd: 'Year to date', ttm: 'Trailing 12 months' };
const money = n => `$${Math.round(Number(n) || 0).toLocaleString()}`;

export default function QuickBooksSetup() {
  const [status, setStatus]   = useState(null);
  const [profit, setProfit]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState(false);
  const [syncing, setSyncing] = useState(false);
  const justConnected = new URLSearchParams(window.location.search).get('qbo_connected') === '1';

  async function load() {
    setLoading(true); setError('');
    try {
      const st = await api.getQboStatus();
      setStatus(st);
      if (st.connected) {
        try { setProfit(await api.getQboProfit()); } catch { /* no snapshots yet */ }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function handleConnect() { window.location.href = api.connectQboUrl; }

  async function handleSync() {
    setSyncing(true); setError('');
    try {
      await api.syncQbo();
      setProfit(await api.getQboProfit());
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  }

  async function handleDisconnect() {
    if (!window.confirm('Disconnect QuickBooks? Up-Tracker will stop syncing your books and the synced copy is removed. Your QuickBooks data is untouched.')) return;
    setBusy(true); setError('');
    try {
      await api.disconnectQbo();
      setProfit([]);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const connected = status?.connected === true;

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>QuickBooks &mdash; your full books</h2>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
        Connect QuickBooks Online and Up-Tracker merges your books with your Upwork
        income &mdash; so you see <strong>true net profit</strong> after platform fees
        <em> and</em> business expenses, plus an estimated-tax set-aside. Read-only:
        Up-Tracker never changes anything in QuickBooks.
      </p>

      {error && <p className="error-msg">{error}</p>}

      {justConnected && !error && (
        <div style={{ background: '#DCFCE7', border: '1px solid #16a34a', color: '#166534',
          borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
          ✓ QuickBooks connected. Click <strong>Refresh from QuickBooks</strong> to pull your first numbers.
        </div>
      )}

      {/* ── Connection status ── */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '18px 20px', marginBottom: 24, background: 'var(--panel)' }}>
        {loading ? (
          <span style={{ color: 'var(--muted)', fontSize: 13 }}>Checking connection…</span>
        ) : connected ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                Connected{status.environment ? <span style={{ color: 'var(--muted-2)', fontWeight: 400 }}> &middot; {status.environment}</span> : null}
              </div>
              <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 6, lineHeight: 1.7 }}>
                {status.realm_id && <>Company (realm): <b>{status.realm_id}</b><br /></>}
                {status.last_sync_utc
                  ? <>Last sync: {new Date(status.last_sync_utc).toLocaleString()}</>
                  : <>Not synced yet — click Refresh.</>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={handleSync} disabled={syncing}>
                {syncing ? 'Refreshing…' : 'Refresh from QuickBooks'}
              </button>
              <button className="btn-outline btn-danger" onClick={handleDisconnect} disabled={busy}>
                {busy ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--muted-2)', display: 'inline-block' }} />
                Not connected
              </div>
              <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 6 }}>
                Connect to pull your Profit &amp; Loss and see true net profit.
              </div>
            </div>
            <button className="btn-primary" onClick={handleConnect}>Connect QuickBooks</button>
          </div>
        )}
      </div>

      {/* ── Profit & Loss snapshots ── */}
      {connected && profit.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
            color: 'var(--muted)', borderBottom: '1px solid var(--border)', paddingBottom: 5, marginBottom: 12 }}>
            Profit &amp; Loss
          </div>
          {profit.map(p => (
            <div key={p.grain} className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
                {GRAIN_LABEL[p.grain] || p.grain} &middot; {p.period_start} → {p.period_end}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                <Metric label="Income"        value={money(p.total_income)}  />
                <Metric label="Expenses"      value={money(p.total_expense)} />
                <Metric label="Net profit"    value={money(p.net_income)} accent="#16a34a" />
                <Metric label="Set aside (tax)" value={money(p.tax_set_aside)} accent="#A66A00" />
              </div>
            </div>
          ))}
          <p className="hint">Net profit and the tax set-aside are estimates from your QuickBooks P&amp;L — not tax advice.</p>
        </div>
      )}

      {/* ── How it works ── */}
      <Section title="How connecting works">
        <ol style={olStyle}>
          <li>Click <b>Connect QuickBooks</b> above.</li>
          <li>You're taken to Intuit's secure sign-in and asked to authorize Up-Tracker (read-only).</li>
          <li>Approve, and Intuit sends you back here — connected. Then click <b>Refresh from QuickBooks</b>.</li>
        </ol>
        <p style={{ ...pStyle, color: 'var(--muted-2)' }}>
          Up-Tracker syncs nightly, and you can refresh on demand any time. Disconnecting removes the synced copy.
        </p>
      </Section>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4, color: accent || 'var(--text)' }}>{value}</div>
    </div>
  );
}

const olStyle = { margin: '0 0 10px', paddingLeft: 18, color: 'var(--text)', fontSize: 13, lineHeight: 1.9 };
const pStyle  = { color: 'var(--text)', fontSize: 13, lineHeight: 1.7, margin: '0 0 6px' };

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
        color: 'var(--muted)', borderBottom: '1px solid var(--border)', paddingBottom: 5, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}
